import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '../lib/supabase';

export type UserRole = '관리자' | '영업자' | '상담원' | 'AI expert';

interface User {
  id: string;
  name: string;
  user_id: string;
  role: '관리자' | '영업자' | '상담원' | 'AI expert';
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  login: (userId: string, password: string) => Promise<boolean>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const savedAuth = localStorage.getItem('ace_auth');
    const savedUser = localStorage.getItem('ace_user');
    if (savedAuth === 'true' && savedUser) {
      setIsAuthenticated(true);
      setUser(JSON.parse(savedUser));
    }
    setLoading(false);
  }, []);

  const login = async (userId: string, password: string): Promise<boolean> => {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('user_id', userId)
        .eq('password', password)
        .single();

      if (error || !data) {
        return false;
      }

      const userData: User = {
        id: data.id,
        name: data.name,
        user_id: data.user_id,
        role: data.role
      };

      setIsAuthenticated(true);
      setUser(userData);
      localStorage.setItem('ace_auth', 'true');
      localStorage.setItem('ace_user', JSON.stringify(userData));
      return true;
    } catch (error) {
      console.error('Login error:', error);
      return false;
    }
  };

  const logout = () => {
    setIsAuthenticated(false);
    setUser(null);
    localStorage.removeItem('ace_auth');
    localStorage.removeItem('ace_user');
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">
      <div className="text-lg">로딩 중...</div>
    </div>;
  }

  return (
    <AuthContext.Provider value={{ isAuthenticated, user, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
